import React, { useEffect, useState, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, Info, RefreshCw, Plus, Trash2 } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';

// Controllo di quadratura con la banca: il gestionale confronta da solo movimenti, estratti PDF e
// movimenti in attesa, e dice a parole cosa non torna. Logica tutta nel server (quadratura.service.ts).

const COLORE = { attenzione: '#EF4444', info: '#3B82F6', ok: '#22C55E' };
const ICONA = { attenzione: AlertTriangle, info: Info, ok: CheckCircle2 };
const euro = (n) => `${n > 0 ? '+' : ''}${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const dataIt = (d) => d.split('-').reverse().join('/');

export default function ControlloBanca({ aggiorna = 0, onCambiato }) {
  const { user } = useAuth();
  const puoAggiungere = puoFare(user, 'banca', 'create');
  const puoEliminare = puoFare(user, 'banca', 'delete');
  const [q, setQ] = useState(null);
  const [errore, setErrore] = useState('');
  const [caricando, setCaricando] = useState(false);
  const [aggiungendo, setAggiungendo] = useState('');

  const carica = useCallback(async () => {
    setCaricando(true); setErrore('');
    try { setQ((await api.get('/banca/quadratura')).data); }
    catch (e) { setErrore(e.response?.data?.error || e.message); }
    finally { setCaricando(false); }
  }, []);
  useEffect(() => { carica(); }, [carica, aggiorna]);

  const aggiungi = async (m) => {
    const k = `${m.aggiungi.estratto_id}-${m.aggiungi.indice}`;
    if (!window.confirm(`Aggiungere il movimento dell'estratto?\n\n${dataIt(m.data)}  ${euro(m.importo)}\n${m.descrizione}`)) return;
    setAggiungendo(k);
    try { await api.post('/banca/quadratura/aggiungi', m.aggiungi); await carica(); onCambiato?.(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setAggiungendo(''); }
  };

  const elimina = async (m) => {
    if (!window.confirm(`Eliminare questo movimento doppio?\n\n${dataIt(m.data)}  ${euro(m.importo)}\n${m.descrizione}\n\nResta traccia nel registro attività.`)) return;
    setAggiungendo(`x${m.elimina}`);
    try { await api.delete(`/banca/transazioni/${m.elimina}`); await carica(); onCambiato?.(); }
    catch (e) { alert(e.response?.data?.error || e.message); }
    finally { setAggiungendo(''); }
  };

  if (errore) return <div style={{ ...s.box, color: '#EF4444', fontSize: 'var(--font-size-xs)' }}>Controllo con la banca non disponibile: {errore}</div>;
  if (!q) return null;
  const problemi = q.avvisi.filter(a => a.livello !== 'ok');
  const ok = q.avvisi.filter(a => a.livello === 'ok');
  const daSistemare = problemi.filter(a => a.livello === 'attenzione').length;

  return (
    <div style={s.box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: problemi.length ? '10px' : 0 }}>
        <div style={s.titolo}>CONTROLLO CON LA BANCA{q.conto ? ` — ${q.conto.nome}` : ''}</div>
        <span style={{ ...s.chip, color: daSistemare ? '#EF4444' : '#22C55E', borderColor: daSistemare ? '#EF444440' : '#22C55E40' }}>
          {daSistemare ? `${daSistemare} da controllare` : 'Tutto torna'}
        </span>
        <button onClick={carica} title="Ricontrolla" style={s.iconBtn} disabled={caricando}>
          <RefreshCw size={13} style={caricando ? { animation: 'spin 1s linear infinite' } : undefined} />
        </button>
      </div>

      {problemi.map((a, i) => <Avviso key={i} a={a} puoAggiungere={puoAggiungere} aggiungi={aggiungi} aggiungendo={aggiungendo} puoEliminare={puoEliminare} elimina={elimina} />)}

      {ok.length > 0 && (
        <details style={{ marginTop: '8px' }}>
          <summary style={s.summary}>✓ {ok.length} controll{ok.length === 1 ? 'o' : 'i'} a posto</summary>
          {ok.map((a, i) => <Avviso key={i} a={a} />)}
        </details>
      )}

      <details style={{ marginTop: '8px' }}>
        <summary style={s.summary}>Come funziona</summary>
        <ul style={s.come}>
          <li>Importa la lista movimenti Excel di Intesa così com'è, anche se si sovrappone a quella di prima: i movimenti già presenti vengono saltati.</li>
          <li>I movimenti <b>in attesa</b> (la banca non li ha ancora contabilizzati) contano già nel saldo, come in banca; al prossimo import si aggiornano da soli, senza doppioni.</li>
          <li>Carica il PDF di ogni estratto trimestrale (scheda Estratti conto): il gestionale lo confronta riga per riga e ti dice cosa manca o è in più.</li>
          <li>Per ogni trimestre con il PDF confronta il <b>saldo di fine trimestre</b> della banca con quello del gestionale e spiega la differenza: di solito sono movimenti a cavallo del trimestre (competenze, commissioni, POS) che la banca registra qualche giorno dopo. Se la differenza non si spiega così, lo segnala in rosso.</li>
          <li>I bonifici degli ultimi giorni di un file arrivano senza il codice della banca di chi paga (BIC): la banca lo aggiunge dopo. Al file successivo il gestionale riconosce lo stesso bonifico e completa il testo invece di caricarlo due volte; un movimento in attesa che la banca registra in due righe (bonifico + commissione) viene sostituito da quelle due.</li>
          <li>A inizio anno l'Excel "dal 1° gennaio" non contiene le operazioni del 31/12 addebitate a gennaio: il controllo le trova nell'estratto e le aggiungi con un clic.</li>
        </ul>
      </details>
    </div>
  );
}

function Avviso({ a, puoAggiungere, aggiungi, aggiungendo, puoEliminare, elimina }) {
  const Icona = ICONA[a.livello];
  const col = COLORE[a.livello];
  return (
    <div style={{ display: 'flex', gap: '10px', padding: '8px 0', borderTop: '1px solid var(--border,#1E1E1E)' }}>
      <Icona size={16} color={col} style={{ flexShrink: 0, marginTop: '2px' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>{a.titolo}</div>
        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted,#888)', marginTop: '2px', lineHeight: 1.5 }}>{a.testo}</div>
        {a.movimenti?.length > 0 && (
          <details style={{ marginTop: '4px' }} open={a.movimenti.some(m => m.aggiungi || m.elimina) && a.movimenti.length <= 5}>
            <summary style={s.summary}>{a.movimenti.length} moviment{a.movimenti.length === 1 ? 'o' : 'i'}</summary>
            {a.movimenti.map((m, i) => {
              const k = m.aggiungi ? `${m.aggiungi.estratto_id}-${m.aggiungi.indice}` : '';
              return (
                <div key={i} style={s.riga}>
                  <span style={{ whiteSpace: 'nowrap', color: 'var(--text-muted,#888)' }}>{dataIt(m.data)}</span>
                  <span style={{ whiteSpace: 'nowrap', fontWeight: 700, color: m.importo < 0 ? '#EF4444' : '#22C55E' }}>{euro(m.importo)}</span>
                  <span style={{ flex: 1, minWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={m.descrizione}>{m.descrizione}</span>
                  {m.aggiungi && puoAggiungere && (
                    <button onClick={() => aggiungi(m)} disabled={aggiungendo === k} style={s.aggiungi}>
                      <Plus size={12} /> {aggiungendo === k ? 'AGGIUNGO…' : 'AGGIUNGI'}
                    </button>
                  )}
                  {m.elimina && puoEliminare && (
                    <button onClick={() => elimina(m)} disabled={aggiungendo === `x${m.elimina}`} style={s.elimina}>
                      <Trash2 size={12} /> {aggiungendo === `x${m.elimina}` ? 'ELIMINO…' : 'ELIMINA'}
                    </button>
                  )}
                </div>
              );
            })}
          </details>
        )}
      </div>
    </div>
  );
}

const s = {
  box: { background: 'var(--bg-card,#141414)', border: '1px solid var(--border,#1E1E1E)', borderRadius: '10px', padding: '14px 18px', marginBottom: '14px' },
  titolo: { fontSize: 'var(--font-size-xs)', fontWeight: 700, letterSpacing: '2px', color: '#3B82F6' },
  chip: { fontSize: 'var(--font-size-xs)', fontWeight: 700, border: '1px solid', borderRadius: '99px', padding: '2px 10px' },
  iconBtn: { marginLeft: 'auto', background: 'var(--bg-input,#1A1A1A)', border: '1px solid var(--border2,#2A2A2A)', borderRadius: '6px', padding: '5px 7px', color: 'var(--text-muted,#888)', cursor: 'pointer', display: 'inline-flex' },
  summary: { fontSize: 'var(--font-size-xs)', color: 'var(--text-muted,#888)', cursor: 'pointer', fontWeight: 600 },
  come: { margin: '6px 0 0', paddingLeft: '18px', fontSize: 'var(--font-size-xs)', color: 'var(--text-muted,#888)', lineHeight: 1.6 },
  riga: { display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', fontSize: 'var(--font-size-xs)', padding: '4px 0' },
  elimina: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#EF444415', color: '#DC2626', border: '1px solid #EF444440', borderRadius: '6px', padding: '4px 8px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
  aggiungi: { display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#22C55E20', color: '#16A34A', border: '1px solid #22C55E40', borderRadius: '6px', padding: '4px 8px', fontSize: 'var(--font-size-xs)', fontWeight: 700, cursor: 'pointer' },
};
